import type { ModuleMigrationDef } from "@kubdee/sdk";

const INVOICE_COLUMNS = `
  \`id\`, \`invoice_no\`, \`company_id\`, \`branch_id\`, \`invoice_type\`, \`party_type\`, \`party_id\`,
  \`invoice_date\`, \`due_date\`, \`subtotal\`, \`tax_amount\`, \`total_discount\`, \`total_wht\`,
  \`total\`, \`outstanding\`, \`doc_status\`, \`status\`, \`credit_note_of\`, \`debit_note_of\`,
  \`reason\`, \`original_invoice_amount\`, \`corrected_amount\`, \`difference_amount\`, \`reason_code\`,
  \`currency_code\`, \`exchange_rate\`, \`transaction_type\`, \`delivery_date\`, \`tax_point_date\`,
  \`vat_status\`, \`vat_period\`, \`remarks\`, \`reference_module\`, \`reference_doctype\`,
  \`reference_id\`, \`created_by\`, \`created_at\`, \`updated_at\`, \`updated_by\`, \`deleted_at\`,
  \`deleted_by\`
`;

function createInvoiceTableSql(tableName: string, idxPrefix: string, defaultInvoiceType: string, defaultPartyType: string): string {
  return `
CREATE TABLE IF NOT EXISTS \`${tableName}\` (
  \`id\` text PRIMARY KEY NOT NULL,
  \`invoice_no\` text NOT NULL,
  \`company_id\` text NOT NULL,
  \`branch_id\` text,
  \`invoice_type\` text DEFAULT '${defaultInvoiceType}' NOT NULL,
  \`party_type\` text DEFAULT '${defaultPartyType}' NOT NULL,
  \`party_id\` text NOT NULL,
  \`invoice_date\` text NOT NULL,
  \`due_date\` text,
  \`subtotal\` real DEFAULT 0 NOT NULL,
  \`tax_amount\` real DEFAULT 0 NOT NULL,
  \`total_discount\` real DEFAULT 0,
  \`total_wht\` real DEFAULT 0,
  \`total\` real DEFAULT 0 NOT NULL,
  \`outstanding\` real DEFAULT 0 NOT NULL,
  \`doc_status\` integer DEFAULT 0 NOT NULL,
  \`status\` text DEFAULT 'draft' NOT NULL,
  \`credit_note_of\` text,
  \`debit_note_of\` text,
  \`reason\` text,
  \`original_invoice_amount\` real,
  \`corrected_amount\` real,
  \`difference_amount\` real,
  \`reason_code\` text,
  \`currency_code\` text DEFAULT 'THB',
  \`exchange_rate\` real DEFAULT 1,
  \`transaction_type\` text,
  \`delivery_date\` text,
  \`tax_point_date\` text,
  \`vat_status\` text DEFAULT 'due',
  \`vat_period\` text,
  \`remarks\` text,
  \`reference_module\` text,
  \`reference_doctype\` text,
  \`reference_id\` text,
  \`created_by\` text,
  \`created_at\` text NOT NULL,
  \`updated_at\` text NOT NULL,
  \`updated_by\` text,
  \`deleted_at\` text,
  \`deleted_by\` text,
  CONSTRAINT \`${tableName}_owner_chk\` CHECK (\`invoice_type\` = '${defaultInvoiceType}' AND \`party_type\` = '${defaultPartyType}')
);
CREATE UNIQUE INDEX IF NOT EXISTS \`${tableName}_invoice_no_unique\` ON \`${tableName}\` (\`invoice_no\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_status_idx\` ON \`${tableName}\` (\`company_id\`, \`status\`, \`invoice_date\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_party_idx\` ON \`${tableName}\` (\`company_id\`, \`party_type\`, \`party_id\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_type_idx\` ON \`${tableName}\` (\`company_id\`, \`invoice_type\`, \`status\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_branch_idx\` ON \`${tableName}\` (\`company_id\`, \`branch_id\`, \`status\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_credit_note_of_idx\` ON \`${tableName}\` (\`credit_note_of\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_debit_note_of_idx\` ON \`${tableName}\` (\`debit_note_of\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_vat_period_idx\` ON \`${tableName}\` (\`company_id\`, \`vat_period\`, \`vat_status\`);
CREATE INDEX IF NOT EXISTS \`${idxPrefix}_company_tax_point_idx\` ON \`${tableName}\` (\`company_id\`, \`tax_point_date\`, \`vat_status\`);
  `.trim();
}

function backfillSql(tableName: string, invoiceType: string): string {
  return `
INSERT INTO \`${tableName}\` (${INVOICE_COLUMNS})
SELECT ${INVOICE_COLUMNS}
FROM \`accounting_invoices\`
WHERE \`invoice_type\` = '${invoiceType}'
ON CONFLICT(\`id\`) DO UPDATE SET
  \`invoice_no\` = excluded.\`invoice_no\`,
  \`company_id\` = excluded.\`company_id\`,
  \`branch_id\` = excluded.\`branch_id\`,
  \`invoice_type\` = excluded.\`invoice_type\`,
  \`party_type\` = excluded.\`party_type\`,
  \`party_id\` = excluded.\`party_id\`,
  \`invoice_date\` = excluded.\`invoice_date\`,
  \`due_date\` = excluded.\`due_date\`,
  \`subtotal\` = excluded.\`subtotal\`,
  \`tax_amount\` = excluded.\`tax_amount\`,
  \`total_discount\` = excluded.\`total_discount\`,
  \`total_wht\` = excluded.\`total_wht\`,
  \`total\` = excluded.\`total\`,
  \`outstanding\` = excluded.\`outstanding\`,
  \`doc_status\` = excluded.\`doc_status\`,
  \`status\` = excluded.\`status\`,
  \`credit_note_of\` = excluded.\`credit_note_of\`,
  \`debit_note_of\` = excluded.\`debit_note_of\`,
  \`reason\` = excluded.\`reason\`,
  \`original_invoice_amount\` = excluded.\`original_invoice_amount\`,
  \`corrected_amount\` = excluded.\`corrected_amount\`,
  \`difference_amount\` = excluded.\`difference_amount\`,
  \`reason_code\` = excluded.\`reason_code\`,
  \`currency_code\` = excluded.\`currency_code\`,
  \`exchange_rate\` = excluded.\`exchange_rate\`,
  \`transaction_type\` = excluded.\`transaction_type\`,
  \`delivery_date\` = excluded.\`delivery_date\`,
  \`tax_point_date\` = excluded.\`tax_point_date\`,
  \`vat_status\` = excluded.\`vat_status\`,
  \`vat_period\` = excluded.\`vat_period\`,
  \`remarks\` = excluded.\`remarks\`,
  \`reference_module\` = excluded.\`reference_module\`,
  \`reference_doctype\` = excluded.\`reference_doctype\`,
  \`reference_id\` = excluded.\`reference_id\`,
  \`created_by\` = excluded.\`created_by\`,
  \`created_at\` = excluded.\`created_at\`,
  \`updated_at\` = excluded.\`updated_at\`,
  \`updated_by\` = excluded.\`updated_by\`,
  \`deleted_at\` = excluded.\`deleted_at\`,
  \`deleted_by\` = excluded.\`deleted_by\`;
  `.trim();
}

export const migration: ModuleMigrationDef = {
  key: "004_invoice_tables",
  version: "1.3.0",
  ignoreErrors: ["no such table: accounting_invoices"],
  sql: `
${createInvoiceTableSql("sales_invoices", "si", "sales", "customer")}
${backfillSql("sales_invoices", "sales")}

${createInvoiceTableSql("sales_credit_notes", "scn", "credit_note", "customer")}
${backfillSql("sales_credit_notes", "credit_note")}
  `.trim(),
};
